import type { Booking, BookingSummary, CreateBookingPayload, CreateBookingResponse, CancelBookingResponse, ReceiptTextResponse } from "@/types/booking"
import type { AuthTokens } from "@/types/authTokens"
import type { LoginCredentials } from "@/types/loginCredentials"
import type { Config } from "@/types/config"
import type { FetchBookingsBody } from "@/types/request/fetchBookingsBody"
import type { GetBookingBody } from "@/types/request/getBookingBody"
import type { AddressSearchBody } from "@/types/request/addressSearchBody"
import type { AddressStreetNumberBody } from "@/types/request/addressStreetNumberBody"
import type { AddressSearchResult } from "@/types/address"
import type { PriceBody, PriceObject } from "@/types/price"
import { performRequest } from "@/utils/RequestUtils"
import { getOrCreateClientId } from "@/utils/StorageUtils"
import Endpoint from "@/enums/Endpoint"

// Package transport customers are Reception users in the booking terminal (UserTypeDto.Reception)
const USER_TYPE_RECEPTION = 1

export async function requestLogin(username: string, password: string): Promise<AuthTokens> {
  const body: LoginCredentials = {
    username,
    password,
    userType: USER_TYPE_RECEPTION,
    clientId: getOrCreateClientId()
  }
  return performRequest<AuthTokens>(Endpoint.LOGIN, "POST", body)
}

export async function requestConfig(): Promise<Config> {
  return performRequest<Config>(Endpoint.CONFIG, "GET")
}

export async function fetchBookings(startDate: string, endDate: string): Promise<BookingSummary[]> {
  const body: FetchBookingsBody = { SearchStartTime: startDate, SearchEndTime: endDate }
  return performRequest<BookingSummary[]>(Endpoint.FETCH_BOOKINGS, "POST", body)
}

export async function fetchBooking(body: GetBookingBody): Promise<Booking> {
  return performRequest<Booking>(Endpoint.GET_BOOKING, "POST", body)
}

export async function createBooking(data: CreateBookingPayload): Promise<CreateBookingResponse> {
  return performRequest<CreateBookingResponse>(Endpoint.CREATE_BOOKING, "POST", data, { keepalive: true })
}

// Found by number within an hour of the pickup time, as the API looks it up; 401 without canSeeReceipt, 404 until the
// taximeter has closed the trip
export async function requestReceiptText(bookingNumber: number, pickupDateTime: string): Promise<ReceiptTextResponse> {
  return performRequest<ReceiptTextResponse>(Endpoint.GET_RECEIPT_TEXT, "POST", { bookingNumber, pickupDateTime })
}

export async function cancelBooking(sutiOrderId: string): Promise<CancelBookingResponse> {
  return performRequest<CancelBookingResponse>(Endpoint.CANCEL_BOOKING, "DELETE", { SutiOrderId: sutiOrderId })
}

export async function calculatePrice(body: PriceBody): Promise<PriceObject> {
  return performRequest<PriceObject>(Endpoint.CALCULATE_PRICE, "POST", body)
}

export async function requestAddressSearch(body: AddressSearchBody): Promise<AddressSearchResult[]> {
  return performRequest<AddressSearchResult[]>(Endpoint.ADDRESS_SEARCH, "POST", body)
}

export async function requestAddressStreetNumbers(body: AddressStreetNumberBody): Promise<AddressSearchResult[]> {
  return performRequest<AddressSearchResult[]>(Endpoint.ADDRESS_STREET_NUMBER, "POST", body)
}
